import React, { useEffect, useMemo, useState } from "react";

import { Link, useLocation, useNavigate } from "react-router-dom";

import {
    FaArrowLeft,
    FaArrowRight,
    FaCheck,
    FaCheckCircle,
    FaChevronRight,
    FaEnvelope,
    FaEye,
    FaEyeSlash,
    FaFacebookF,
    FaGoogle,
    FaHeadset,
    FaIdCard,
    FaLock,
    FaMapMarkerAlt,
    FaMotorcycle,
    FaPhone,
    FaRoute,
    FaShieldAlt,
    FaUser,
    FaWallet,
    FaBolt,
    FaCalendarAlt,
    FaVenusMars,
    FaHome,
    FaCar,
    FaCreditCard,
    FaUserFriends,
    FaFileAlt,
    FaCamera,
    FaUpload,
    FaInfoCircle,
} from "react-icons/fa";

import { useSelector, useDispatch } from "react-redux";

import toast from "react-hot-toast";

import {
    rider_register,
    messageClear,
} from "../store/reducers/authReducer";
import Headers from "../components/Headers";
import Footer from "../components/Footer";


const Register = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    // =========================================================
    // AUTH STATE
    // =========================================================

    const {
        loader,
        successMessage,
        errorMessage,
        userInfo,
    } = useSelector((state) => state.auth);

    // =========================================================
    // REDIRECT STATE
    // =========================================================

    const from = location.state?.from || null;
    const checkoutState = location.state?.checkoutState || null;

    // =========================================================
    // STEP
    // =========================================================

    const [currentStep, setCurrentStep] = useState(1);

    const totalSteps = 5;

    // =========================================================
    // FORM STATE
    // =========================================================

    const [state, setState] = useState({
        // STEP 1
        name: "",
        mobile: "",
        email: "",
        password: "",
        confirmPassword: "",

        // STEP 2
        dateOfBirth: "",
        gender: "",
        nidNumber: "",
        presentAddress: "",
        permanentAddress: "",
        sameAddress: false,

        // STEP 3
        vehicleType: "",
        vehicleRegistrationNumber: "",
        drivingLicenseNumber: "",
        drivingLicenseExpiry: "",
        riderExperience: "",

        // STEP 4
        emergencyName: "",
        emergencyRelationship: "",
        emergencyMobile: "",

        method: "",
        accountName: "",
        accountNumber: "",

        // STEP 5
        profilePhoto: null,
        nidFront: null,
        nidBack: null,
        drivingLicenseDocument: null,
        vehiclePhoto: null,
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);

    // =========================================================
    // FILE STATE
    // =========================================================

    const [filePreviews, setFilePreviews] = useState({
        profilePhoto: "",
        nidFront: "",
        nidBack: "",
        drivingLicenseDocument: "",
        vehiclePhoto: "",
    });

    // =========================================================
    // INPUT HANDLER
    // =========================================================

    const inputHandle = (e) => {
        const { name, value, type, checked } = e.target;

        setState((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    // =========================================================
    // FILE HANDLER
    // =========================================================

    const fileHandle = (e, field) => {
        const file = e.target.files?.[0];

        if (!file) return;

        const allowedTypes = [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/webp",
        ];

        if (field === "drivingLicenseDocument") {
            const allowedDocuments = [
                ...allowedTypes,
                "application/pdf",
            ];

            if (!allowedDocuments.includes(file.type)) {
                toast.error(
                    "Please upload JPG, PNG, WEBP or PDF file."
                );
                return;
            }
        } else {
            if (!allowedTypes.includes(file.type)) {
                toast.error(
                    "Please upload JPG, PNG or WEBP image."
                );
                return;
            }
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error("File size must be less than 5MB.");
            return;
        }

        setState((prev) => ({
            ...prev,
            [field]: file,
        }));

        if (file.type.startsWith("image/")) {
            const preview = URL.createObjectURL(file);

            setFilePreviews((prev) => ({
                ...prev,
                [field]: preview,
            }));
        }
    };

    // =========================================================
    // REMOVE FILE
    // =========================================================

    const removeFile = (field) => {
        setState((prev) => ({
            ...prev,
            [field]: null,
        }));

        setFilePreviews((prev) => ({
            ...prev,
            [field]: "",
        }));
    };

    // =========================================================
    // PASSWORD STRENGTH
    // =========================================================

    const passwordStrength = useMemo(() => {
        const password = state.password;

        if (!password) {
            return {
                width: "0%",
                label: "6+ characters",
                color: "bg-gray-200",
                text: "text-gray-400",
            };
        }

        if (
            password.length >= 10 &&
            /[A-Z]/.test(password) &&
            /[a-z]/.test(password) &&
            /\d/.test(password) &&
            /[^A-Za-z0-9]/.test(password)
        ) {
            return {
                width: "100%",
                label: "Strong password",
                color: "bg-green-500",
                text: "text-green-600",
            };
        }

        if (password.length >= 8) {
            return {
                width: "80%",
                label: "Good password",
                color: "bg-emerald-400",
                text: "text-emerald-600",
            };
        }

        if (password.length >= 6) {
            return {
                width: "60%",
                label: "Fair password",
                color: "bg-orange-400",
                text: "text-orange-500",
            };
        }

        return {
            width: "30%",
            label: "Weak password",
            color: "bg-red-400",
            text: "text-red-500",
        };
    }, [state.password]);

    // =========================================================
    // STEP INFORMATION
    // =========================================================

    const steps = [
        {
            number: 1,
            title: "Account",
            subtitle: "Basic details",
            icon: FaUser,
        },
        {
            number: 2,
            title: "Personal",
            subtitle: "Identity & address",
            icon: FaIdCard,
        },
        {
            number: 3,
            title: "Vehicle",
            subtitle: "Rider details",
            icon: FaMotorcycle,
        },
        {
            number: 4,
            title: "Contact",
            subtitle: "Emergency & payout",
            icon: FaWallet,
        },
        {
            number: 5,
            title: "Documents",
            subtitle: "Verification",
            icon: FaFileAlt,
        },
    ];

    // =========================================================
    // COMMON INPUT CLASS
    // =========================================================

    const inputClass =
        "h-[55px] w-full rounded-2xl border border-gray-200 bg-gray-50 pl-11 pr-4 text-sm font-semibold text-gray-900 outline-none transition-all duration-300 placeholder:text-gray-400 hover:border-gray-300 focus:border-red-500 focus:bg-white focus:ring-4 focus:ring-red-500/10 disabled:cursor-not-allowed disabled:opacity-60";

    const selectClass =
        "h-[55px] w-full appearance-none rounded-2xl border border-gray-200 bg-gray-50 px-4 pr-10 text-sm font-semibold text-gray-900 outline-none transition-all duration-300 hover:border-gray-300 focus:border-red-500 focus:bg-white focus:ring-4 focus:ring-red-500/10 disabled:cursor-not-allowed disabled:opacity-60";

    // =========================================================
    // VALIDATE STEP 1
    // =========================================================

    const validateStep1 = () => {
        const name = state.name.trim();
        const mobile = state.mobile.trim();
        const email = state.email.trim().toLowerCase();
        const password = state.password;
        const confirmPassword = state.confirmPassword;

        if (!name) {
            toast.error("Please enter your full name.");
            return false;
        }

        if (name.length < 2) {
            toast.error("Name must be at least 2 characters.");
            return false;
        }

        if (!mobile) {
            toast.error("Please enter your mobile number.");
            return false;
        }

        if (!/^01[3-9]\d{8}$/.test(mobile)) {
            toast.error(
                "Please enter a valid 11-digit Bangladeshi mobile number."
            );
            return false;
        }

        if (!email) {
            toast.error("Please enter your email address.");
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailRegex.test(email)) {
            toast.error("Please enter a valid email address.");
            return false;
        }

        if (!password.trim()) {
            toast.error("Please create a password.");
            return false;
        }

        if (password.length < 6) {
            toast.error("Password must be at least 6 characters.");
            return false;
        }

        if (password !== confirmPassword) {
            toast.error("Passwords do not match.");
            return false;
        }

        return true;
    };

    // =========================================================
    // VALIDATE STEP 2
    // =========================================================

    const validateStep2 = () => {
        if (!state.dateOfBirth) {
            toast.error("Please select your date of birth.");
            return false;
        }

        if (!state.gender) {
            toast.error("Please select your gender.");
            return false;
        }

        if (!state.nidNumber.trim()) {
            toast.error("Please enter your NID number.");
            return false;
        }

        const nid = state.nidNumber.trim();

        if (!/^\d{10}$|^\d{13}$|^\d{17}$/.test(nid)) {
            toast.error(
                "Please enter a valid 10, 13 or 17 digit NID number."
            );
            return false;
        }

        if (!state.presentAddress.trim()) {
            toast.error("Please enter your present address.");
            return false;
        }

        if (
            !state.sameAddress &&
            !state.permanentAddress.trim()
        ) {
            toast.error("Please enter your permanent address.");
            return false;
        }

        return true;
    };

    // =========================================================
    // VALIDATE STEP 3
    // =========================================================

    const validateStep3 = () => {
        if (!state.vehicleType) {
            toast.error("Please select your vehicle type.");
            return false;
        }

        if (
            state.vehicleType === "motorcycle" &&
            !state.vehicleRegistrationNumber.trim()
        ) {
            toast.error(
                "Please enter your vehicle registration number."
            );
            return false;
        }

        if (
            state.vehicleType === "motorcycle" &&
            !state.drivingLicenseNumber.trim()
        ) {
            toast.error(
                "Please enter your driving license number."
            );
            return false;
        }

        if (
            state.vehicleType === "motorcycle" &&
            !state.drivingLicenseExpiry
        ) {
            toast.error(
                "Please select your driving license expiry date."
            );
            return false;
        }

        if (!state.riderExperience) {
            toast.error("Please select your riding experience.");
            return false;
        }

        return true;
    };

    // =========================================================
    // VALIDATE STEP 4
    // =========================================================

    const validateStep4 = () => {
        if (!state.emergencyName.trim()) {
            toast.error("Please enter your emergency contact name.");
            return false;
        }

        if (!state.emergencyRelationship) {
            toast.error(
                "Please select your emergency contact relationship."
            );
            return false;
        }

        if (
            !/^01[3-9]\d{8}$/.test(
                state.emergencyMobile.trim()
            )
        ) {
            toast.error(
                "Please enter a valid emergency contact number."
            );
            return false;
        }

        if (!state.method) {
            toast.error("Please select a payment method.");
            return false;
        }

        if (!state.accountName.trim()) {
            toast.error("Please enter the account holder name.");
            return false;
        }

        if (!state.accountNumber.trim()) {
            toast.error("Please enter the payment account number.");
            return false;
        }

        if (
            state.accountNumber.trim() ===
            state.mobile.trim()
        ) {
            // Allowed intentionally.
        }

        return true;
    };

    // =========================================================
    // VALIDATE STEP 5
    // =========================================================

    const validateStep5 = () => {
        if (!state.profilePhoto) {
            toast.error("Please upload your profile photo.");
            return false;
        }

        if (!state.nidFront) {
            toast.error("Please upload the front side of your NID.");
            return false;
        }

        if (!state.nidBack) {
            toast.error("Please upload the back side of your NID.");
            return false;
        }

        if (
            state.vehicleType === "motorcycle" &&
            !state.drivingLicenseDocument
        ) {
            toast.error(
                "Please upload your driving license document."
            );
            return false;
        }

        if (!state.vehiclePhoto) {
            toast.error("Please upload your vehicle photo.");
            return false;
        }

        if (!acceptedTerms) {
            toast.error(
                "Please agree to the Terms & Conditions and Privacy Policy."
            );
            return false;
        }

        return true;
    };

    // =========================================================
    // NEXT STEP
    // =========================================================

    const nextStep = () => {
        if (loader) return;

        let valid = true;

        if (currentStep === 1) {
            valid = validateStep1();
        }

        if (currentStep === 2) {
            valid = validateStep2();
        }

        if (currentStep === 3) {
            valid = validateStep3();
        }

        if (currentStep === 4) {
            valid = validateStep4();
        }

        if (!valid) return;

        if (currentStep < totalSteps) {
            setCurrentStep((prev) => prev + 1);

            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        }
    };

    // =========================================================
    // PREVIOUS STEP
    // =========================================================

    const previousStep = () => {
        if (loader) return;

        if (currentStep > 1) {
            setCurrentStep((prev) => prev - 1);

            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        }
    };

    // =========================================================
    // REGISTER
    // =========================================================


const register = (e) => {
  e.preventDefault();

  if (loader) return;
  if (!validateStep5()) return;

  const permanentAddress = state.sameAddress
    ? state.presentAddress.trim()
    : state.permanentAddress.trim();

  const formData = new FormData();

  // Basic information
  formData.append("name", state.name.trim());
  formData.append("mobile", state.mobile.trim());
  formData.append("email", state.email.trim().toLowerCase());
  formData.append("password", state.password);

  // Personal information
  formData.append("dateOfBirth", state.dateOfBirth);
  formData.append("gender", state.gender);

  if (state.nidNumber?.trim()) {
    formData.append("nidNumber", state.nidNumber.trim());
  }

  // Address
  formData.append("presentAddress", state.presentAddress.trim());
  formData.append("permanentAddress", permanentAddress);

  // Vehicle information
  formData.append("vehicleType", state.vehicleType);
  formData.append(
    "vehicleRegistrationNumber",
    state.vehicleRegistrationNumber?.trim() || ""
  );
  formData.append(
    "drivingLicenseNumber",
    state.drivingLicenseNumber?.trim() || ""
  );

  if (state.drivingLicenseExpiry) {
    formData.append("drivingLicenseExpiry", state.drivingLicenseExpiry);
  }

  formData.append("riderExperience", state.riderExperience);

  // Nested objects must be JSON strings in FormData
  formData.append(
    "emergencyContact",
    JSON.stringify({
      name: state.emergencyName.trim(),
      relationship: state.emergencyRelationship.trim(),
      mobile: state.emergencyMobile.trim(),
    })
  );

  formData.append(
    "payment",
    JSON.stringify({
      method: state.method,
      accountName: state.accountName.trim(),
      accountNumber: state.accountNumber.trim(),
    })
  );

  // Optional image files
  const imageFields = [
    "profilePhoto",
    "nidFront",
    "nidBack",
    "drivingLicenseDocument",
    "vehiclePhoto",
  ];

  imageFields.forEach((field) => {
    const file = state[field];

    if (file instanceof File) {
      formData.append(field, file);
    }
  });

  dispatch(rider_register(formData));
};

    // =========================================================
    // SUCCESS / ERROR MESSAGE
    // =========================================================

    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage);

            dispatch(messageClear());
        }

        if (errorMessage) {
            toast.error(errorMessage);

            dispatch(messageClear());
        }
    }, [
        successMessage,
        errorMessage,
        dispatch,
    ]);

    // =========================================================
    // SAME ADDRESS
    // =========================================================

    useEffect(() => {
        if (state.sameAddress) {
            setState((prev) => ({
                ...prev,
                permanentAddress:
                    prev.presentAddress,
            }));
        }
    }, [
        state.sameAddress,
        state.presentAddress,
    ]);

    // =========================================================
    // USER SUCCESS REDIRECT
    // =========================================================

    useEffect(() => {
        if (!userInfo) return;

        /*
         * Keep your existing auth flow here if your reducer
         * automatically logs the rider in after registration.
         *
         * If your reducer already handles redirect elsewhere,
         * this block can remain unused.
         */
    }, [userInfo]);

    // =========================================================
    // FILE UPLOAD COMPONENT
    // =========================================================

    const UploadBox = ({
        field,
        title,
        subtitle,
        required = true,
        icon: Icon = FaUpload,
    }) => {
        const file = state[field];
        const preview = filePreviews[field];

        return (
            <div className="group">
                <label className="mb-2.5 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600">
                    {title}

                    {required && (
                        <span className="ml-1 text-red-500">
                            *
                        </span>
                    )}
                </label>

                <label
                    htmlFor={field}
                    className={`relative flex min-h-[145px] cursor-pointer flex-col items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed transition-all duration-300 ${
                        file
                            ? "border-green-300 bg-green-50/40"
                            : "border-gray-200 bg-gray-50 hover:border-red-300 hover:bg-red-50/30"
                    }`}
                >
                    <input
                        id={field}
                        type="file"
                        accept={
                            field ===
                            "drivingLicenseDocument"
                                ? "image/*,.pdf"
                                : "image/*"
                        }
                        onChange={(e) =>
                            fileHandle(e, field)
                        }
                        disabled={loader}
                        className="hidden"
                    />

                    {preview ? (
                        <>
                            <img
                                src={preview}
                                alt={title}
                                className="absolute inset-0 h-full w-full object-cover"
                            />

                            <div className="absolute inset-0 flex items-center justify-center bg-black/35 opacity-0 transition group-hover:opacity-100">
                                <div className="rounded-xl bg-white px-4 py-2 text-xs font-black text-gray-800 shadow-xl">
                                    Change file
                                </div>
                            </div>
                        </>
                    ) : file ? (
                        <div className="flex flex-col items-center px-5 text-center">
                            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-green-100 text-green-600">
                                <FaFileAlt size={18} />
                            </div>

                            <p className="max-w-[220px] truncate text-xs font-black text-gray-800">
                                {file.name}
                            </p>

                            <p className="mt-1 text-[9px] font-medium text-gray-400">
                                File selected
                            </p>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center px-5 text-center">
                            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-500 transition group-hover:scale-105 group-hover:bg-red-100">
                                <Icon size={18} />
                            </div>

                            <p className="text-xs font-black text-gray-700">
                                {subtitle}
                            </p>

                            <p className="mt-1 text-[9px] font-medium text-gray-400">
                                JPG, PNG, WEBP
                                {field ===
                                    "drivingLicenseDocument" &&
                                    " or PDF"}{" "}
                                • Max 5MB
                            </p>
                        </div>
                    )}
                </label>

                {file && (
                    <button
                        type="button"
                        onClick={() =>
                            removeFile(field)
                        }
                        className="mt-2 text-[10px] font-black text-red-500 transition hover:text-red-700"
                    >
                        Remove selected file
                    </button>
                )}
            </div>
        );
    };

    // =========================================================
    // STEP TITLE
    // =========================================================

    const getStepTitle = () => {
        const titles = {
            1: {
                eyebrow: "Step 01 / Account",
                title: "Create your",
                highlight: "Rider Account.",
                description:
                    "Let's start with your basic account information.",
            },

            2: {
                eyebrow: "Step 02 / Personal",
                title: "Tell us about",
                highlight: "yourself.",
                description:
                    "Your identity and address help us verify your rider profile.",
            },

            3: {
                eyebrow: "Step 03 / Vehicle",
                title: "Set up your",
                highlight: "rider profile.",
                description:
                    "Tell us how you will complete deliveries with FoodBite24.",
            },

            4: {
                eyebrow: "Step 04 / Contact & Payout",
                title: "Stay connected",
                highlight: "and get paid.",
                description:
                    "Add an emergency contact and your preferred payout information.",
            },

            5: {
                eyebrow: "Step 05 / Verification",
                title: "Complete your",
                highlight: "verification.",
                description:
                    "Upload your documents so our team can review your rider application.",
            },
        };

        return titles[currentStep];
    };

    const stepTitle = getStepTitle();

    // =========================================================
    // RENDER
    // =========================================================

    return (
        <div className="min-h-screen bg-[#fff8f8] text-gray-900">

            {/* =====================================================
                HEADER
            ===================================================== */}
<Headers/>


            {/* =====================================================
                MAIN
            ===================================================== */}

            <main className="relative overflow-hidden">

                {/* BACKGROUND */}

                <div className="pointer-events-none absolute inset-0 overflow-hidden">

                    <div className="absolute -left-32 top-20 h-80 w-80 rounded-full bg-red-100/70 blur-[100px]" />

                    <div className="absolute -right-32 top-72 h-96 w-96 rounded-full bg-red-50 blur-[100px]" />

                    <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-orange-50/60 blur-[100px]" />

                </div>


                {/* CONTAINER */}

                <div className="relative mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-10 lg:px-8 lg:py-14">

                    {/* MAIN CARD */}

                    <div className="overflow-hidden rounded-[30px] border border-gray-100 bg-white shadow-[0_25px_90px_rgba(15,23,42,0.08)]">

                        <div className="grid lg:grid-cols-[0.78fr_1.22fr]">


                            {/* =================================================
                                LEFT RIDER PANEL
                            ================================================= */}

                            <section className="relative hidden min-h-[800px] overflow-hidden bg-[#151515] px-10 py-12 text-white lg:block xl:px-14">

                                {/* RED GLOWS */}

                                <div className="absolute -left-32 -top-32 h-[420px] w-[420px] rounded-full bg-red-600/25 blur-[100px]" />

                                <div className="absolute -bottom-40 -right-32 h-[450px] w-[450px] rounded-full bg-red-600/15 blur-[110px]" />


                                {/* GRID */}

                                <div
                                    className="pointer-events-none absolute inset-0 opacity-[0.035]"
                                    style={{
                                        backgroundImage:
                                            "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
                                        backgroundSize:
                                            "44px 44px",
                                    }}
                                />


                                <div className="relative z-10 flex h-full flex-col">

                                    {/* BADGE */}

                                    <div className="flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3.5 py-2 backdrop-blur-md">

                                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-600">
                                            <FaBolt size={8} />
                                        </span>

                                        <span className="text-[9px] font-black uppercase tracking-[0.18em] text-white/75">
                                            Become a Rider
                                        </span>

                                    </div>


                                    {/* TITLE */}

                                    <div className="mt-9 max-w-lg">

                                        <p className="text-[10px] font-black uppercase tracking-[0.28em] text-red-400">
                                            Ride • Earn • Grow
                                        </p>

                                        <h1 className="mt-4 text-[42px] font-black leading-[1.05] tracking-[-0.04em] xl:text-[52px]">

                                            Build your

                                            <br />

                                            <span className="text-red-500">
                                                rider journey.
                                            </span>

                                        </h1>

                                        <p className="mt-5 max-w-md text-sm font-medium leading-7 text-white/50">
                                            Join FoodBite24 as a delivery
                                            partner and manage your rider
                                            journey from one simple platform.
                                        </p>

                                    </div>


                                    {/* RIDER VISUAL */}

                                    <div className="relative mt-12 flex flex-1 items-center justify-center">

                                        {/* ROUTE */}

                                        <div className="absolute left-[5%] right-[5%] top-1/2 border-t border-dashed border-white/10" />


                                        {/* LOCATION */}

                                        <div className="absolute left-0 top-12 rounded-2xl border border-white/10 bg-white/[0.07] p-3 backdrop-blur-xl">

                                            <div className="flex items-center gap-3">

                                                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-500/15 text-red-400">
                                                    <FaMapMarkerAlt size={13} />
                                                </div>

                                                <div>
                                                    <p className="text-[8px] font-bold uppercase tracking-wider text-white/35">
                                                        Delivery
                                                    </p>

                                                    <p className="mt-0.5 text-xs font-black">
                                                        Smart Route
                                                    </p>
                                                </div>

                                            </div>
                                        </div>


                                        {/* EARNINGS */}

                                        <div className="absolute right-0 top-28 rounded-2xl border border-white/10 bg-white/[0.07] p-3 backdrop-blur-xl">

                                            <div className="flex items-center gap-3">

                                                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-green-500/10 text-green-400">
                                                    <FaWallet size={13} />
                                                </div>

                                                <div>
                                                    <p className="text-[8px] font-bold uppercase tracking-wider text-white/35">
                                                        Earnings
                                                    </p>

                                                    <p className="mt-0.5 text-xs font-black">
                                                        Track Easily
                                                    </p>
                                                </div>

                                            </div>
                                        </div>


                                        {/* BIKE */}

                                        <div className="relative flex h-52 w-52 items-center justify-center rounded-full border border-white/[0.06] bg-white/[0.025]">

                                            <div className="absolute inset-5 rounded-full border border-red-500/20" />

                                            <div className="absolute inset-12 rounded-full border border-red-500/10" />

                                            <div className="flex h-32 w-32 items-center justify-center rounded-full bg-red-600 text-white shadow-[0_20px_60px_rgba(220,38,38,0.35)]">

                                                <FaMotorcycle size={55} />

                                            </div>

                                        </div>

                                    </div>


                                    {/* FEATURES */}

                                    <div className="mt-8 grid grid-cols-3 gap-3 border-t border-white/10 pt-6">

                                        <div>
                                            <FaRoute
                                                size={14}
                                                className="mb-2 text-red-500"
                                            />

                                            <p className="text-[11px] font-black">
                                                Smart Routes
                                            </p>

                                            <p className="mt-1 text-[8px] text-white/35">
                                                Easy navigation
                                            </p>
                                        </div>

                                        <div>
                                            <FaWallet
                                                size={14}
                                                className="mb-2 text-red-500"
                                            />

                                            <p className="text-[11px] font-black">
                                                Earnings
                                            </p>

                                            <p className="mt-1 text-[8px] text-white/35">
                                                Track income
                                            </p>
                                        </div>

                                        <div>
                                            <FaHeadset
                                                size={14}
                                                className="mb-2 text-red-500"
                                            />

                                            <p className="text-[11px] font-black">
                                                Support
                                            </p>

                                            <p className="mt-1 text-[8px] text-white/35">
                                                Rider help
                                            </p>
                                        </div>

                                    </div>

                                </div>
                            </section>


                            {/* =================================================
                                RIGHT REGISTRATION
                            ================================================= */}

                            <section className="bg-white px-5 py-8 sm:px-8 sm:py-10 lg:px-10 xl:px-14">

                                <div className="mx-auto w-full max-w-[560px]">


                                    {/* MOBILE BRAND */}

                                    <div className="mb-7 flex items-center gap-3 lg:hidden">

                                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-600 text-white shadow-lg">
                                            <FaMotorcycle size={19} />
                                        </div>

                                        <div>
                                            <p className="text-lg font-black leading-none">
                                                FoodBite
                                                <span className="text-red-600">
                                                    24
                                                </span>
                                            </p>

                                            <p className="mt-1 text-[8px] font-black uppercase tracking-[0.2em] text-gray-400">
                                                Rider Partner
                                            </p>
                                        </div>

                                    </div>


                                    {/* MOBILE PROGRESS */}

                                    <div className="mb-7 overflow-x-auto pb-1 lg:hidden">

                                        <div className="flex min-w-[480px] items-center">

                                            {steps.map((step, index) => {

                                                const Icon = step.icon;

                                                const active =
                                                    currentStep ===
                                                    step.number;

                                                const completed =
                                                    currentStep >
                                                    step.number;

                                                return (
                                                    <React.Fragment
                                                        key={
                                                            step.number
                                                        }
                                                    >

                                                        <div className="flex items-center gap-2">

                                                            <div
                                                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-black transition ${
                                                                    active ||
                                                                    completed
                                                                        ? "bg-red-600 text-white shadow-lg shadow-red-600/20"
                                                                        : "bg-gray-100 text-gray-400"
                                                                }`}
                                                            >

                                                                {completed ? (
                                                                    <FaCheck
                                                                        size={
                                                                            11
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <Icon
                                                                        size={
                                                                            13
                                                                        }
                                                                    />
                                                                )}

                                                            </div>

                                                            <div className="hidden sm:block">

                                                                <p
                                                                    className={`text-[10px] font-black ${
                                                                        active ||
                                                                        completed
                                                                            ? "text-gray-900"
                                                                            : "text-gray-400"
                                                                    }`}
                                                                >
                                                                    {
                                                                        step.title
                                                                    }
                                                                </p>

                                                                <p className="text-[8px] font-medium text-gray-400">
                                                                    {
                                                                        step.subtitle
                                                                    }
                                                                </p>

                                                            </div>

                                                        </div>

                                                        {index <
                                                            steps.length -
                                                                1 && (
                                                            <div
                                                                className={`mx-2 h-px min-w-[25px] flex-1 ${
                                                                    currentStep >
                                                                    step.number
                                                                        ? "bg-red-500"
                                                                        : "bg-gray-200"
                                                                }`}
                                                            />
                                                        )}

                                                    </React.Fragment>
                                                );
                                            })}

                                        </div>

                                    </div>


                                    {/* HEADING */}

                                    <div className="mb-7">

                                        <div className="mb-4 flex items-center gap-2">

                                            <span className="h-1 w-8 rounded-full bg-red-600" />

                                            <span className="text-[9px] font-black uppercase tracking-[0.22em] text-red-600">
                                                {stepTitle.eyebrow}
                                            </span>

                                        </div>

                                        <h2 className="text-[31px] font-black leading-[1.08] tracking-[-0.03em] text-gray-950 sm:text-[37px]">

                                            {stepTitle.title}

                                            <br />

                                            <span className="text-red-600">
                                                {stepTitle.highlight}
                                            </span>

                                        </h2>

                                        <p className="mt-3 max-w-md text-sm leading-6 text-gray-500">
                                            {stepTitle.description}
                                        </p>

                                    </div>


                                    {/* DESKTOP PROGRESS */}

                                    <div className="mb-8 hidden rounded-2xl border border-gray-100 bg-gray-50/70 p-3 lg:block">

                                        <div className="flex items-center">

                                            {steps.map((step, index) => {

                                                const Icon = step.icon;

                                                const active =
                                                    currentStep ===
                                                    step.number;

                                                const completed =
                                                    currentStep >
                                                    step.number;

                                                return (
                                                    <React.Fragment
                                                        key={
                                                            step.number
                                                        }
                                                    >

                                                        <button
                                                            type="button"
                                                            disabled={
                                                                loader
                                                            }
                                                            onClick={() => {
                                                                if (
                                                                    step.number <
                                                                    currentStep
                                                                ) {
                                                                    setCurrentStep(
                                                                        step.number
                                                                    );
                                                                }
                                                            }}
                                                            className={`group flex items-center gap-2 ${
                                                                step.number <
                                                                currentStep
                                                                    ? "cursor-pointer"
                                                                    : "cursor-default"
                                                            }`}
                                                        >

                                                            <div
                                                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-black transition ${
                                                                    active ||
                                                                    completed
                                                                        ? "bg-red-600 text-white shadow-lg shadow-red-600/20"
                                                                        : "bg-white text-gray-400"
                                                                }`}
                                                            >

                                                                {completed ? (
                                                                    <FaCheck
                                                                        size={
                                                                            11
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <Icon
                                                                        size={
                                                                            13
                                                                        }
                                                                    />
                                                                )}

                                                            </div>

                                                            <div className="hidden xl:block text-left">

                                                                <p
                                                                    className={`text-[10px] font-black ${
                                                                        active
                                                                            ? "text-gray-900"
                                                                            : completed
                                                                            ? "text-red-600"
                                                                            : "text-gray-400"
                                                                    }`}
                                                                >
                                                                    {
                                                                        step.title
                                                                    }
                                                                </p>

                                                                <p className="text-[8px] font-medium text-gray-400">
                                                                    {
                                                                        step.subtitle
                                                                    }
                                                                </p>

                                                            </div>

                                                        </button>

                                                        {index <
                                                            steps.length -
                                                                1 && (
                                                            <div
                                                                className={`mx-2 h-px flex-1 ${
                                                                    currentStep >
                                                                    step.number
                                                                        ? "bg-red-500"
                                                                        : "bg-gray-200"
                                                                }`}
                                                            />
                                                        )}

                                                    </React.Fragment>
                                                );
                                            })}

                                        </div>
                                    </div>


                                    {/* FORM */}

                                    <form
                                        onSubmit={register}
                                        className="space-y-5"
                                    >


                                        {/* =================================================
                                            STEP 1
                                        ================================================= */}

                                        {currentStep === 1 && (
                                            <div className="space-y-5">

                                                {/* NAME */}

                                                <div>
                                                    <label
                                                        htmlFor="name"
                                                        className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                    >
                                                        Full Name
                                                    </label>

                                                    <div className="group relative">

                                                        <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 transition group-focus-within:text-red-600">
                                                            <FaUser
                                                                size={
                                                                    14
                                                                }
                                                            />
                                                        </div>

                                                        <input
                                                            id="name"
                                                            name="name"
                                                            type="text"
                                                            value={
                                                                state.name
                                                            }
                                                            onChange={
                                                                inputHandle
                                                            }
                                                            placeholder="Enter your full name"
                                                            autoComplete="name"
                                                            disabled={
                                                                loader
                                                            }
                                                            className={
                                                                inputClass
                                                            }
                                                        />

                                                    </div>
                                                </div>


                                                {/* MOBILE */}

                                                <div>
                                                    <label
                                                        htmlFor="mobile"
                                                        className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                    >
                                                        Mobile Number
                                                    </label>

                                                    <div className="group relative">

                                                        <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 transition group-focus-within:text-red-600">
                                                            <FaPhone
                                                                size={
                                                                    14
                                                                }
                                                            />
                                                        </div>

                                                        <input
                                                            id="mobile"
                                                            name="mobile"
                                                            type="tel"
                                                            value={
                                                                state.mobile
                                                            }
                                                            onChange={
                                                                inputHandle
                                                            }
                                                            placeholder="01XXXXXXXXX"
                                                            autoComplete="tel"
                                                            inputMode="numeric"
                                                            maxLength={
                                                                11
                                                            }
                                                            disabled={
                                                                loader
                                                            }
                                                            className={
                                                                inputClass
                                                            }
                                                        />

                                                    </div>

                                                    <p className="mt-1.5 text-[9px] font-medium text-gray-400">
                                                        Enter your 11-digit Bangladeshi mobile number.
                                                    </p>
                                                </div>


                                                {/* EMAIL */}

                                                <div>
                                                    <label
                                                        htmlFor="email"
                                                        className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                    >
                                                        Email Address
                                                    </label>

                                                    <div className="group relative">

                                                        <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 transition group-focus-within:text-red-600">
                                                            <FaEnvelope
                                                                size={
                                                                    14
                                                                }
                                                            />
                                                        </div>

                                                        <input
                                                            id="email"
                                                            name="email"
                                                            type="email"
                                                            value={
                                                                state.email
                                                            }
                                                            onChange={
                                                                inputHandle
                                                            }
                                                            placeholder="Enter your email address"
                                                            autoComplete="email"
                                                            disabled={
                                                                loader
                                                            }
                                                            className={
                                                                inputClass
                                                            }
                                                        />

                                                    </div>
                                                </div>


                                                {/* PASSWORD */}

                                                <div>
                                                    <label
                                                        htmlFor="password"
                                                        className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                    >
                                                        Password
                                                    </label>

                                                    <div className="group relative">

                                                        <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 transition group-focus-within:text-red-600">
                                                            <FaLock
                                                                size={
                                                                    14
                                                                }
                                                            />
                                                        </div>

                                                        <input
                                                            id="password"
                                                            name="password"
                                                            type={
                                                                showPassword
                                                                    ? "text"
                                                                    : "password"
                                                            }
                                                            value={
                                                                state.password
                                                            }
                                                            onChange={
                                                                inputHandle
                                                            }
                                                            placeholder="Create your password"
                                                            autoComplete="new-password"
                                                            disabled={
                                                                loader
                                                            }
                                                            className={`${inputClass} pr-12`}
                                                        />

                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setShowPassword(
                                                                    (
                                                                        prev
                                                                    ) =>
                                                                        !prev
                                                                )
                                                            }
                                                            disabled={
                                                                loader
                                                            }
                                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 transition hover:text-red-600"
                                                        >
                                                            {showPassword ? (
                                                                <FaEyeSlash
                                                                    size={
                                                                        16
                                                                    }
                                                                />
                                                            ) : (
                                                                <FaEye
                                                                    size={
                                                                        16
                                                                    }
                                                                />
                                                            )}
                                                        </button>

                                                    </div>

                                                    <div className="mt-2 flex items-center gap-2">

                                                        <div className="h-1 flex-1 overflow-hidden rounded-full bg-gray-100">

                                                            <div
                                                                className={`h-full rounded-full transition-all duration-300 ${passwordStrength.color}`}
                                                                style={{
                                                                    width:
                                                                        passwordStrength.width,
                                                                }}
                                                            />

                                                        </div>

                                                        <span
                                                            className={`text-[9px] font-bold ${passwordStrength.text}`}
                                                        >
                                                            {
                                                                passwordStrength.label
                                                            }
                                                        </span>

                                                    </div>
                                                </div>


                                                {/* CONFIRM PASSWORD */}

                                                <div>
                                                    <label
                                                        htmlFor="confirmPassword"
                                                        className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                    >
                                                        Confirm Password
                                                    </label>

                                                    <div className="group relative">

                                                        <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 transition group-focus-within:text-red-600">
                                                            <FaShieldAlt
                                                                size={
                                                                    14
                                                                }
                                                            />
                                                        </div>

                                                        <input
                                                            id="confirmPassword"
                                                            name="confirmPassword"
                                                            type={
                                                                showConfirmPassword
                                                                    ? "text"
                                                                    : "password"
                                                            }
                                                            value={
                                                                state.confirmPassword
                                                            }
                                                            onChange={
                                                                inputHandle
                                                            }
                                                            placeholder="Confirm your password"
                                                            autoComplete="new-password"
                                                            disabled={
                                                                loader
                                                            }
                                                            className={`${inputClass} pr-12`}
                                                        />

                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setShowConfirmPassword(
                                                                    (
                                                                        prev
                                                                    ) =>
                                                                        !prev
                                                                )
                                                            }
                                                            disabled={
                                                                loader
                                                            }
                                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 transition hover:text-red-600"
                                                        >
                                                            {showConfirmPassword ? (
                                                                <FaEyeSlash
                                                                    size={
                                                                        16
                                                                    }
                                                                />
                                                            ) : (
                                                                <FaEye
                                                                    size={
                                                                        16
                                                                    }
                                                                />
                                                            )}
                                                        </button>

                                                    </div>
                                                </div>

                                            </div>
                                        )}


                                        {/* =================================================
                                            STEP 2
                                        ================================================= */}

                                        {currentStep === 2 && (
                                            <div className="space-y-5">

                                                <div className="grid gap-4 sm:grid-cols-2">

                                                    {/* DOB */}

                                                    <div>
                                                        <label
                                                            htmlFor="dateOfBirth"
                                                            className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                        >
                                                            Date of Birth
                                                        </label>

                                                        <div className="group relative">

                                                            <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-600">
                                                                <FaCalendarAlt
                                                                    size={
                                                                        14
                                                                    }
                                                                />
                                                            </div>

                                                            <input
                                                                id="dateOfBirth"
                                                                name="dateOfBirth"
                                                                type="date"
                                                                value={
                                                                    state.dateOfBirth
                                                                }
                                                                onChange={
                                                                    inputHandle
                                                                }
                                                                disabled={
                                                                    loader
                                                                }
                                                                className={
                                                                    inputClass
                                                                }
                                                            />

                                                        </div>
                                                    </div>


                                                    {/* GENDER */}

                                                    <div>
                                                        <label
                                                            htmlFor="gender"
                                                            className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                        >
                                                            Gender
                                                        </label>

                                                        <div className="relative">

                                                            <div className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-gray-400">
                                                                <FaVenusMars
                                                                    size={
                                                                        15
                                                                    }
                                                                />
                                                            </div>

                                                            <select
                                                                id="gender"
                                                                name="gender"
                                                                value={
                                                                    state.gender
                                                                }
                                                                onChange={
                                                                    inputHandle
                                                                }
                                                                disabled={
                                                                    loader
                                                                }
                                                                className={`${selectClass} pl-11`}
                                                            >

                                                                <option value="">
                                                                    Select gender
                                                                </option>

                                                                <option value="male">
                                                                    Male
                                                                </option>

                                                                <option value="female">
                                                                    Female
                                                                </option>

                                                                <option value="other">
                                                                    Other
                                                                </option>

                                                            </select>

                                                            <FaChevronRight
                                                                className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 rotate-90 text-gray-400"
                                                                size={
                                                                    10
                                                                }
                                                            />

                                                        </div>
                                                    </div>

                                                </div>


                                                {/* NID */}

                                                <div>
                                                    <label
                                                        htmlFor="nidNumber"
                                                        className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                    >
                                                        National ID / NID Number
                                                    </label>

                                                    <div className="group relative">

                                                        <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-600">
                                                            <FaIdCard
                                                                size={
                                                                    14
                                                                }
                                                            />
                                                        </div>

                                                        <input
                                                            id="nidNumber"
                                                            name="nidNumber"
                                                            type="text"
                                                            value={
                                                                state.nidNumber
                                                            }
                                                            onChange={
                                                                inputHandle
                                                            }
                                                            placeholder="Enter your NID number"
                                                            inputMode="numeric"
                                                            disabled={
                                                                loader
                                                            }
                                                            className={
                                                                inputClass
                                                            }
                                                        />

                                                    </div>

                                                    <p className="mt-1.5 text-[9px] text-gray-400">
                                                        Your NID will be used for rider verification.
                                                    </p>
                                                </div>


                                                {/* PRESENT ADDRESS */}

                                                <div>
                                                    <label
                                                        htmlFor="presentAddress"
                                                        className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                    >
                                                        Present Address
                                                    </label>

                                                    <div className="group relative">

                                                        <div className="pointer-events-none absolute left-4 top-4 text-gray-400 group-focus-within:text-red-600">
                                                            <FaMapMarkerAlt
                                                                size={
                                                                    14
                                                                }
                                                            />
                                                        </div>

                                                        <textarea
                                                            id="presentAddress"
                                                            name="presentAddress"
                                                            value={
                                                                state.presentAddress
                                                            }
                                                            onChange={
                                                                inputHandle
                                                            }
                                                            placeholder="Enter your current address"
                                                            rows={
                                                                3
                                                            }
                                                            disabled={
                                                                loader
                                                            }
                                                            className="w-full resize-none rounded-2xl border border-gray-200 bg-gray-50 px-11 py-4 text-sm font-semibold text-gray-900 outline-none transition-all duration-300 placeholder:text-gray-400 hover:border-gray-300 focus:border-red-500 focus:bg-white focus:ring-4 focus:ring-red-500/10"
                                                        />

                                                    </div>
                                                </div>


                                                {/* SAME ADDRESS */}

                                                <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50/70 p-3.5 transition hover:border-red-100 hover:bg-red-50/30">

                                                    <input
                                                        type="checkbox"
                                                        name="sameAddress"
                                                        checked={
                                                            state.sameAddress
                                                        }
                                                        onChange={
                                                            inputHandle
                                                        }
                                                        disabled={
                                                            loader
                                                        }
                                                        className="h-4 w-4 cursor-pointer accent-red-600"
                                                    />

                                                    <span className="text-xs font-bold text-gray-600">
                                                        Permanent address is the same as present address
                                                    </span>

                                                </label>


                                                {/* PERMANENT */}

                                                {!state.sameAddress && (
                                                    <div>
                                                        <label
                                                            htmlFor="permanentAddress"
                                                            className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                        >
                                                            Permanent Address
                                                        </label>

                                                        <div className="group relative">

                                                            <div className="pointer-events-none absolute left-4 top-4 text-gray-400 group-focus-within:text-red-600">
                                                                <FaHome
                                                                    size={
                                                                        14
                                                                    }
                                                                />
                                                            </div>

                                                            <textarea
                                                                id="permanentAddress"
                                                                name="permanentAddress"
                                                                value={
                                                                    state.permanentAddress
                                                                }
                                                                onChange={
                                                                    inputHandle
                                                                }
                                                                placeholder="Enter your permanent address"
                                                                rows={
                                                                    3
                                                                }
                                                                disabled={
                                                                    loader
                                                                }
                                                                className="w-full resize-none rounded-2xl border border-gray-200 bg-gray-50 px-11 py-4 text-sm font-semibold text-gray-900 outline-none transition-all duration-300 placeholder:text-gray-400 hover:border-gray-300 focus:border-red-500 focus:bg-white focus:ring-4 focus:ring-red-500/10"
                                                            />

                                                        </div>
                                                    </div>
                                                )}

                                            </div>
                                        )}


                                        {/* =================================================
                                            STEP 3
                                        ================================================= */}

                                        {currentStep === 3 && (
                                            <div className="space-y-5">

                                                {/* VEHICLE TYPE */}

                                                <div>
                                                    <label className="mb-3 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600">
                                                        Choose your vehicle
                                                    </label>

                                                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">

                                                        {[
                                                            {
                                                                value: "motorcycle",
                                                                label: "Motorcycle",
                                                                icon: FaMotorcycle,
                                                            },
                                                            {
                                                                value: "bicycle",
                                                                label: "Bicycle",
                                                                icon: FaRoute,
                                                            },
                                                            {
                                                                value: "electricBike",
                                                                label: "E-Bike",
                                                                icon: FaBolt,
                                                            },
                                                            {
                                                                value: "other",
                                                                label: "Other",
                                                                icon: FaCar,
                                                            },
                                                        ].map(
                                                            (
                                                                vehicle
                                                            ) => {
                                                                const Icon =
                                                                    vehicle.icon;

                                                                const active =
                                                                    state.vehicleType ===
                                                                    vehicle.value;

                                                                return (
                                                                    <button
                                                                        key={
                                                                            vehicle.value
                                                                        }
                                                                        type="button"
                                                                        disabled={
                                                                            loader
                                                                        }
                                                                        onClick={() =>
                                                                            setState(
                                                                                (
                                                                                    prev
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    vehicleType:
                                                                                        vehicle.value,
                                                                                })
                                                                            )
                                                                        }
                                                                        className={`relative flex min-h-[105px] flex-col items-center justify-center rounded-2xl border p-3 text-center transition-all ${
                                                                            active
                                                                                ? "border-red-500 bg-red-50 text-red-600 shadow-[0_10px_25px_rgba(220,38,38,0.10)]"
                                                                                : "border-gray-200 bg-gray-50 text-gray-500 hover:border-red-200 hover:bg-red-50/30"
                                                                        }`}
                                                                    >

                                                                        {active && (
                                                                            <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-white">
                                                                                <FaCheck
                                                                                    size={
                                                                                        8
                                                                                    }
                                                                                />
                                                                            </span>
                                                                        )}

                                                                        <Icon
                                                                            size={
                                                                                22
                                                                            }
                                                                        />

                                                                        <span className="mt-2 text-[10px] font-black">
                                                                            {
                                                                                vehicle.label
                                                                            }
                                                                        </span>

                                                                    </button>
                                                                );
                                                            }
                                                        )}

                                                    </div>
                                                </div>


                                                {/* MOTORCYCLE DETAILS */}

                                                {state.vehicleType ===
                                                    "motorcycle" && (
                                                    <div className="space-y-5 rounded-[22px] border border-red-100 bg-red-50/30 p-4 sm:p-5">

                                                        <div className="flex items-start gap-3">

                                                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-600">
                                                                <FaMotorcycle
                                                                    size={
                                                                        14
                                                                    }
                                                                />
                                                            </div>

                                                            <div>
                                                                <p className="text-xs font-black text-gray-800">
                                                                    Motorcycle details
                                                                </p>

                                                                <p className="mt-1 text-[9px] leading-4 text-gray-500">
                                                                    These details are required for motorcycle-based deliveries.
                                                                </p>
                                                            </div>

                                                        </div>


                                                        <div className="grid gap-4 sm:grid-cols-2">

                                                            <div>
                                                                <label
                                                                    htmlFor="vehicleRegistrationNumber"
                                                                    className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                                >
                                                                    Registration Number
                                                                </label>

                                                                <input
                                                                    id="vehicleRegistrationNumber"
                                                                    name="vehicleRegistrationNumber"
                                                                    value={
                                                                        state.vehicleRegistrationNumber
                                                                    }
                                                                    onChange={
                                                                        inputHandle
                                                                    }
                                                                    placeholder="e.g. DHAKA-METRO-LA-12-3456"
                                                                    disabled={
                                                                        loader
                                                                    }
                                                                    className="h-[55px] w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm font-semibold uppercase outline-none transition focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                                                                />
                                                            </div>


                                                            <div>
                                                                <label
                                                                    htmlFor="drivingLicenseNumber"
                                                                    className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                                >
                                                                    Driving License Number
                                                                </label>

                                                                <input
                                                                    id="drivingLicenseNumber"
                                                                    name="drivingLicenseNumber"
                                                                    value={
                                                                        state.drivingLicenseNumber
                                                                    }
                                                                    onChange={
                                                                        inputHandle
                                                                    }
                                                                    placeholder="Enter license number"
                                                                    disabled={
                                                                        loader
                                                                    }
                                                                    className="h-[55px] w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm font-semibold outline-none transition focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                                                                />
                                                            </div>

                                                        </div>


                                                        <div>
                                                            <label
                                                                htmlFor="drivingLicenseExpiry"
                                                                className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                            >
                                                                License Expiry Date
                                                            </label>

                                                            <input
                                                                id="drivingLicenseExpiry"
                                                                name="drivingLicenseExpiry"
                                                                type="date"
                                                                value={
                                                                    state.drivingLicenseExpiry
                                                                }
                                                                onChange={
                                                                    inputHandle
                                                                }
                                                                disabled={
                                                                    loader
                                                                }
                                                                className="h-[55px] w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm font-semibold outline-none transition focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                                                            />
                                                        </div>

                                                    </div>
                                                )}


                                                {/* EXPERIENCE */}

                                                <div>
                                                    <label
                                                        htmlFor="riderExperience"
                                                        className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                    >
                                                        Delivery / Riding Experience
                                                    </label>

                                                    <div className="relative">

                                                        <select
                                                            id="riderExperience"
                                                            name="riderExperience"
                                                            value={
                                                                state.riderExperience
                                                            }
                                                            onChange={
                                                                inputHandle
                                                            }
                                                            disabled={
                                                                loader
                                                            }
                                                            className={
                                                                selectClass
                                                            }
                                                        >

                                                            <option value="">
                                                                Select your experience
                                                            </option>

                                                            <option value="no-experience">
                                                                No experience
                                                            </option>

                                                            <option value="less-than-1-year">
                                                                Less than 1 year
                                                            </option>

                                                            <option value="1-2-years">
                                                                1–2 years
                                                            </option>

                                                            <option value="2-5-years">
                                                                2–5 years
                                                            </option>

                                                            <option value="5-plus-years">
                                                                5+ years
                                                            </option>

                                                        </select>

                                                        <FaChevronRight
                                                            className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 rotate-90 text-gray-400"
                                                            size={
                                                                10
                                                            }
                                                        />

                                                    </div>
                                                </div>


                                                {/* INFO */}

                                                <div className="flex items-start gap-3 rounded-2xl border border-blue-100 bg-blue-50/60 p-4">

                                                    <FaInfoCircle
                                                        className="mt-0.5 shrink-0 text-blue-500"
                                                        size={
                                                            14
                                                        }
                                                    />

                                                    <p className="text-[10px] font-medium leading-5 text-blue-700">
                                                        You can start with a bicycle, e-bike or motorcycle. Vehicle information may be reviewed before rider approval.
                                                    </p>

                                                </div>

                                            </div>
                                        )}


                                        {/* =================================================
                                            STEP 4
                                        ================================================= */}

                                        {currentStep === 4 && (
                                            <div className="space-y-5">

                                                {/* EMERGENCY */}

                                                <div className="rounded-[22px] border border-gray-100 bg-gray-50/60 p-4 sm:p-5">

                                                    <div className="mb-5 flex items-start gap-3">

                                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-600">
                                                            <FaUserFriends
                                                                size={
                                                                    15
                                                                }
                                                            />
                                                        </div>

                                                        <div>
                                                            <p className="text-xs font-black text-gray-900">
                                                                Emergency Contact
                                                            </p>

                                                            <p className="mt-1 text-[9px] leading-4 text-gray-400">
                                                                Someone we can contact if there is an emergency.
                                                            </p>
                                                        </div>

                                                    </div>


                                                    <div className="space-y-4">

                                                        <div>
                                                            <label
                                                                htmlFor="emergencyName"
                                                                className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                            >
                                                                Contact Name
                                                            </label>

                                                            <input
                                                                id="emergencyName"
                                                                name="emergencyName"
                                                                value={
                                                                    state.emergencyName
                                                                }
                                                                onChange={
                                                                    inputHandle
                                                                }
                                                                placeholder="Emergency contact full name"
                                                                disabled={
                                                                    loader
                                                                }
                                                                className="h-[52px] w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm font-semibold outline-none transition focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                                                            />
                                                        </div>


                                                        <div className="grid gap-4 sm:grid-cols-2">

                                                            <div>
                                                                <label
                                                                    htmlFor="emergencyRelationship"
                                                                    className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                                >
                                                                    Relationship
                                                                </label>

                                                                <div className="relative">

                                                                    <select
                                                                        id="emergencyRelationship"
                                                                        name="emergencyRelationship"
                                                                        value={
                                                                            state.emergencyRelationship
                                                                        }
                                                                        onChange={
                                                                            inputHandle
                                                                        }
                                                                        disabled={
                                                                            loader
                                                                        }
                                                                        className={
                                                                            selectClass
                                                                        }
                                                                    >

                                                                        <option value="">
                                                                            Select
                                                                        </option>

                                                                        <option value="parent">
                                                                            Parent
                                                                        </option>

                                                                        <option value="spouse">
                                                                            Spouse
                                                                        </option>

                                                                        <option value="sibling">
                                                                            Sibling
                                                                        </option>

                                                                        <option value="friend">
                                                                            Friend
                                                                        </option>

                                                                        <option value="other">
                                                                            Other
                                                                        </option>

                                                                    </select>

                                                                    <FaChevronRight
                                                                        className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 rotate-90 text-gray-400"
                                                                        size={
                                                                            10
                                                                        }
                                                                    />

                                                                </div>
                                                            </div>


                                                            <div>
                                                                <label
                                                                    htmlFor="emergencyMobile"
                                                                    className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                                >
                                                                    Contact Number
                                                                </label>

                                                                <input
                                                                    id="emergencyMobile"
                                                                    name="emergencyMobile"
                                                                    type="tel"
                                                                    value={
                                                                        state.emergencyMobile
                                                                    }
                                                                    onChange={
                                                                        inputHandle
                                                                    }
                                                                    placeholder="01XXXXXXXXX"
                                                                    inputMode="numeric"
                                                                    maxLength={
                                                                        11
                                                                    }
                                                                    disabled={
                                                                        loader
                                                                    }
                                                                    className="h-[55px] w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm font-semibold outline-none transition focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                                                                />
                                                            </div>

                                                        </div>

                                                    </div>
                                                </div>


                                                {/* PAYMENT */}

                                                <div className="rounded-[22px] border border-gray-100 bg-gray-50/60 p-4 sm:p-5">

                                                    <div className="mb-5 flex items-start gap-3">

                                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-green-50 text-green-600">
                                                            <FaWallet
                                                                size={
                                                                    15
                                                                }
                                                            />
                                                        </div>

                                                        <div>
                                                            <p className="text-xs font-black text-gray-900">
                                                                Payout Information
                                                            </p>

                                                            <p className="mt-1 text-[9px] leading-4 text-gray-400">
                                                                Choose where your rider earnings should be paid.
                                                            </p>
                                                        </div>

                                                    </div>


                                                    <div className="space-y-4">

                                                        <div>
                                                            <label
                                                                htmlFor="method"
                                                                className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                            >
                                                                Payment Method
                                                            </label>

                                                            <div className="grid grid-cols-3 gap-2">

                                                                {[
                                                                    "bKash",
                                                                    "Nagad",
                                                                    "Bank",
                                                                ].map(
                                                                    (
                                                                        method
                                                                    ) => (
                                                                        <button
                                                                            key={
                                                                                method
                                                                            }
                                                                            type="button"
                                                                            disabled={
                                                                                loader
                                                                            }
                                                                            onClick={() =>
                                                                                setState(
                                                                                    (
                                                                                        prev
                                                                                    ) => ({
                                                                                        ...prev,
                                                                                        method:
                                                                                            method,
                                                                                    })
                                                                                )
                                                                            }
                                                                            className={`h-12 rounded-xl border text-[10px] font-black transition ${
                                                                                state.method ===
                                                                                method
                                                                                    ? "border-red-500 bg-red-50 text-red-600"
                                                                                    : "border-gray-200 bg-white text-gray-500 hover:border-red-200"
                                                                            }`}
                                                                        >
                                                                            {
                                                                                method
                                                                            }
                                                                        </button>
                                                                    )
                                                                )}

                                                            </div>
                                                        </div>


                                                        <div>
                                                            <label
                                                                htmlFor="accountName"
                                                                className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                            >
                                                                Account Holder Name
                                                            </label>

                                                            <div className="group relative">

                                                                <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-600">
                                                                    <FaUser
                                                                        size={
                                                                            13
                                                                        }
                                                                    />
                                                                </div>

                                                                <input
                                                                    id="accountName"
                                                                    name="accountName"
                                                                    value={
                                                                        state.accountName
                                                                    }
                                                                    onChange={
                                                                        inputHandle
                                                                    }
                                                                    placeholder="Name on payment account"
                                                                    disabled={
                                                                        loader
                                                                    }
                                                                    className={
                                                                        inputClass
                                                                    }
                                                                />

                                                            </div>
                                                        </div>


                                                        <div>
                                                            <label
                                                                htmlFor="accountNumber"
                                                                className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                            >
                                                                Account / Mobile Number
                                                            </label>

                                                            <div className="group relative">

                                                                <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-600">
                                                                    <FaCreditCard
                                                                        size={
                                                                            14
                                                                        }
                                                                    />
                                                                </div>

                                                                <input
                                                                    id="accountNumber"
                                                                    name="accountNumber"
                                                                    value={
                                                                        state.accountNumber
                                                                    }
                                                                    onChange={
                                                                        inputHandle
                                                                    }
                                                                    placeholder={
                                                                        state.method ===
                                                                        "Bank"
                                                                            ? "Enter bank account number"
                                                                            : "Enter mobile number"
                                                                    }
                                                                    disabled={
                                                                        loader
                                                                    }
                                                                    className={
                                                                        inputClass
                                                                    }
                                                                />

                                                            </div>
                                                        </div>

                                                    </div>
                                                </div>

                                            </div>
                                        )}


                                        {/* =================================================
                                            STEP 5
                                        ================================================= */}

                                        {currentStep === 5 && (
                                            <div className="space-y-5">

                                                {/* DOCUMENT INFO */}

                                                <div className="flex items-start gap-3 rounded-2xl border border-amber-100 bg-amber-50/60 p-4">

                                                    <FaInfoCircle
                                                        className="mt-0.5 shrink-0 text-amber-500"
                                                        size={
                                                            14
                                                        }
                                                    />

                                                    <div>
                                                        <p className="text-[10px] font-black text-amber-800">
                                                            Document verification
                                                        </p>

                                                        <p className="mt-1 text-[9px] font-medium leading-5 text-amber-700">
                                                            Upload clear and readable documents. Your information will be reviewed before your rider application is approved.
                                                        </p>
                                                    </div>

                                                </div>


                                                {/* PROFILE */}

                                                <div className="grid gap-4 sm:grid-cols-2">

                                                    <UploadBox
                                                        field="profilePhoto"
                                                        title="Profile Photo"
                                                        subtitle="Upload your photo"
                                                        icon={
                                                            FaCamera
                                                        }
                                                    />

                                                    <UploadBox
                                                        field="vehiclePhoto"
                                                        title="Vehicle Photo"
                                                        subtitle="Upload vehicle photo"
                                                        icon={
                                                            FaMotorcycle
                                                        }
                                                    />

                                                </div>


                                                {/* NID */}

                                                <div className="rounded-[22px] border border-gray-100 bg-gray-50/50 p-4 sm:p-5">

                                                    <div className="mb-4">

                                                        <p className="text-xs font-black text-gray-900">
                                                            NID Documents
                                                        </p>

                                                        <p className="mt-1 text-[9px] font-medium text-gray-400">
                                                            Upload both sides of your National ID.
                                                        </p>

                                                    </div>

                                                    <div className="grid gap-4 sm:grid-cols-2">

                                                        <UploadBox
                                                            field="nidFront"
                                                            title="NID Front"
                                                            subtitle="Upload front side"
                                                            icon={
                                                                FaIdCard
                                                            }
                                                        />

                                                        <UploadBox
                                                            field="nidBack"
                                                            title="NID Back"
                                                            subtitle="Upload back side"
                                                            icon={
                                                                FaIdCard
                                                            }
                                                        />

                                                    </div>

                                                </div>


                                                {/* LICENSE */}

                                                {state.vehicleType ===
                                                    "motorcycle" && (
                                                    <UploadBox
                                                        field="drivingLicenseDocument"
                                                        title="Driving License"
                                                        subtitle="Upload driving license"
                                                        icon={
                                                            FaFileAlt
                                                        }
                                                    />
                                                )}


                                                {/* TERMS */}

                                                <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-gray-100 bg-gray-50/70 p-4 transition hover:border-red-100 hover:bg-red-50/30">

                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            acceptedTerms
                                                        }
                                                        onChange={(e) =>
                                                            setAcceptedTerms(
                                                                e
                                                                    .target
                                                                    .checked
                                                            )
                                                        }
                                                        disabled={
                                                            loader
                                                        }
                                                        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-red-600"
                                                    />

                                                    <span className="text-[11px] leading-5 text-gray-500">

                                                        I agree to the{" "}

                                                        <Link
                                                            to="/terms"
                                                            className="font-black text-red-600 hover:text-red-700"
                                                        >
                                                            Terms & Conditions
                                                        </Link>

                                                        {" "}and{" "}

                                                        <Link
                                                            to="/privacy-policy"
                                                            className="font-black text-red-600 hover:text-red-700"
                                                        >
                                                            Privacy Policy
                                                        </Link>

                                                        . I confirm that the information provided is accurate.

                                                    </span>

                                                </label>

                                            </div>
                                        )}


                                        {/* =================================================
                                            NAVIGATION
                                        ================================================= */}

                                        <div className="flex items-center gap-3 border-t border-gray-100 pt-5">

                                            {currentStep > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={
                                                        previousStep
                                                    }
                                                    disabled={
                                                        loader
                                                    }
                                                    className="flex h-[56px] items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white px-5 text-xs font-black text-gray-700 transition hover:border-gray-300 hover:bg-gray-50 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                                                >
                                                    <FaArrowLeft
                                                        size={
                                                            11
                                                        }
                                                    />

                                                    Back
                                                </button>
                                            )}


                                            {currentStep <
                                                totalSteps ? (
                                                <button
                                                    type="button"
                                                    onClick={
                                                        nextStep
                                                    }
                                                    disabled={
                                                        loader
                                                    }
                                                    className="group relative flex h-[56px] flex-1 items-center justify-center gap-2 overflow-hidden rounded-2xl bg-red-600 text-sm font-black text-white shadow-[0_14px_35px_rgba(220,38,38,0.22)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-red-700 hover:shadow-[0_18px_40px_rgba(220,38,38,0.28)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
                                                >

                                                    <span className="pointer-events-none absolute inset-y-0 -left-20 w-16 rotate-12 bg-white/20 blur-lg transition-all duration-700 group-hover:left-[110%]" />

                                                    Continue

                                                    <FaArrowRight
                                                        size={
                                                            12
                                                        }
                                                        className="transition-transform duration-300 group-hover:translate-x-1"
                                                    />

                                                </button>
                                            ) : (
                                                <button
                                                    type="submit"
                                                    
                                                    disabled={
                                                        loader
                                                    }
                                                    className="group relative flex h-[56px] flex-1 items-center justify-center gap-2 overflow-hidden rounded-2xl bg-red-600 text-sm font-black text-white shadow-[0_14px_35px_rgba(220,38,38,0.22)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-red-700 hover:shadow-[0_18px_40px_rgba(220,38,38,0.28)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
                                                >

                                                    <span className="pointer-events-none absolute inset-y-0 -left-20 w-16 rotate-12 bg-white/20 blur-lg transition-all duration-700 group-hover:left-[110%]" />

                                                    {loader ? (
                                                        <>
                                                            <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />

                                                            Submitting Application...
                                                        </>
                                                    ) : (
                                                        <>
                                                            Submit Rider Application

                                                            <FaCheckCircle
                                                                size={
                                                                    14
                                                                }
                                                                className="transition-transform duration-300 group-hover:scale-110"
                                                            />
                                                        </>
                                                    )}

                                                </button>
                                            )}

                                        </div>

                                    </form>


                                    {/* SECURITY */}

                                    <div className="mt-5 flex items-center gap-3 rounded-2xl border border-green-100 bg-green-50/70 px-4 py-3">

                                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white text-green-600 shadow-sm">
                                            <FaShieldAlt
                                                size={13}
                                            />
                                        </div>

                                        <div>
                                            <p className="text-[10px] font-black text-green-700">
                                                Secure Rider Registration
                                            </p>

                                            <p className="mt-0.5 text-[9px] font-medium text-green-600/70">
                                                Your account and verification information are protected.
                                            </p>
                                        </div>

                                    </div>


                                    {/* LOGIN */}

                                    <div className="mt-7 text-center">

                                        <p className="text-xs font-medium text-gray-500">
                                            Already have a rider account?
                                        </p>

                                        <Link
                                            to="/login"
                                            state={{
                                                from,
                                                checkoutState,
                                            }}
                                            className="mt-2 inline-flex items-center gap-2 text-sm font-black text-red-600 transition hover:text-red-700"
                                        >
                                            Sign in to Rider Account

                                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-50">
                                                <FaArrowRight
                                                    size={
                                                        9
                                                    }
                                                />
                                            </span>

                                        </Link>

                                    </div>


                                    {/* TRUST */}

                                    <div className="mt-6 flex items-center justify-center gap-2 text-[9px] font-semibold text-gray-400">

                                        <FaCheckCircle
                                            size={10}
                                            className="text-green-500"
                                        />

                                        Trusted FoodBite24 Rider Portal

                                    </div>

                                </div>
                            </section>

                        </div>
                    </div>
                </div>
            </main>




            {/* =====================================================
                LOADING OVERLAY
            ===================================================== */}

            {loader && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/45 p-4 backdrop-blur-md">

                    <div className="w-full max-w-[290px] rounded-[26px] border border-white/70 bg-white p-8 text-center shadow-[0_30px_100px_rgba(0,0,0,0.20)]">

                        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-600">
                            <FaMotorcycle
                                size={24}
                            />
                        </div>

                        <div className="flex justify-center">

                            <div className="h-7 w-7 animate-spin rounded-full border-[3px] border-red-100 border-t-red-600" />

                        </div>

                        <p className="mt-5 text-sm font-black text-gray-900">
                            Creating your rider account...
                        </p>

                        <p className="mt-1 text-[10px] font-medium text-gray-400">
                            Preparing your rider application
                        </p>

                    </div>
                </div>
            )}
<Footer/>
        </div>
    );
};

export default Register;
